import { platformMap } from '../../constants';
import { titleCaseFromCamel } from '../../utils';

export const TransactionStatus = {
  /** Initial/starting state for DI */
  Upcoming: 'Upcoming',
  /** Initial/starting state for TA */
  Confirmed: 'Confirmed',
  /** Final state for DI */
  Paid: 'Paid',
  /** Final state for TA */
  Completed: 'Completed',
};

export const getAvailableTypes = (platform) => {
  const types = [
    { value: 'all', label: 'All' },
    ...(platform === platformMap.eatclub.value ? [{ value: 'dineIn', label: 'Dine-In' }] : []),
    { value: 'takeAway', label: 'Takeaway' },
    ...(platform === platformMap.whitelabel.value
      ? [{ value: 'tableOrder', label: 'Table Order' }]
      : []),
  ];

  return types;
};

export const formatType = (type) => {
  switch (type) {
    case 'inAppOrder':
      return 'In-App Order';
    case 'dineIn':
      return 'Dine-In';
    case 'takeAway':
      return 'Takeaway';
    case 'tableOrder':
      return 'Table Order';
    default:
      return type;
  }
};

export const formatStatus = (type, status) => {
  if (type === 'dineIn') {
    if (status === 'Confirmed') {
      return 'Upcoming'; // adds more context to the confirmed state for Dine-In
    }
  }

  return status;
};

export const formatIssueStatus = (issueStatus) => {
  if (issueStatus === 'partySizeReported') {
    return 'Party Size Changed';
  }
  return titleCaseFromCamel(issueStatus);
};

export const getOverviewPanelTabs = (transaction, receipts) => {
  const allTabs = [];

  const isEatClubPlatform = transaction?.platform === platformMap.eatclub.value;
  if (isEatClubPlatform) {
    allTabs.push({ label: 'Overview', value: 'overview' });
  }

  // in the by-payment view a row is a single tap so this is one receipt; in the by-booking view
  // a row can carry a receipt per tap, so number the tabs when there's more than one
  if (receipts?.length > 0) {
    const isMultiReceipt = receipts.length > 1;
    receipts.forEach((_, i) => {
      allTabs.push({
        label: !isMultiReceipt ? 'Receipt' : `Receipt ${i + 1}`,
        value: i,
      });
    });
  }

  const hasOrder = !!transaction?.orderId;
  if (hasOrder) {
    allTabs.push({ label: 'Items', value: 'items' });
  }

  return allTabs;
};

export const processInAppPaymentItems = (inAppPaymentItems, discountPercent = null) => {
  const processedItems = inAppPaymentItems.reduce(
    (acc, curr) => {
      if (curr.offerAppliedBy === 'eatclub') {
        const prevTotal = curr.origPrice * curr.qty;
        const total = curr.venuePrice * curr.qty;

        const wasAdjusted = curr.origPrice > curr.inHousePrice && curr.price < curr.inHousePrice;

        acc.ecApplied.prevTotal += prevTotal;
        acc.ecApplied.total += total;

        acc.ecApplied.items.push({
          ...curr,
          prevTotal,
          total,
          wasAdjusted,
        });
      }

      if (curr.offerAppliedBy === 'venue') {
        const prevTotal = curr.origPrice * curr.qty;
        const total = curr.price * curr.qty;

        acc.venueApplied.prevTotal += prevTotal;
        acc.venueApplied.total += total;

        acc.venueApplied.items.push({
          ...curr,
          prevTotal,
          total,
        });
      }

      if (curr.offerAppliedBy === 'exclude') {
        const total = curr.origPrice * curr.qty;

        acc.excluded.total += total;
        acc.excluded.items.push({
          ...curr,
          total,
        });
      }

      return acc;
    },
    {
      ecApplied: {
        prevTotal: 0,
        total: 0,
        items: [],
      },
      venueApplied: {
        prevTotal: 0,
        total: 0,
        items: [],
      },
      excluded: {
        total: 0,
        items: [],
      },
    },
  );

  if (discountPercent !== null && processedItems.ecApplied.prevTotal > 0) {
    // We can't use roundToDecimal(prevTotal * (1 - discountPercent / 100), 2) here because prevTotal
    // is accumulated via floating-point addition and cannot be represented exactly in IEEE 754 (e.g.
    // 100.10 is stored as 100.0999...). For discounts like 30%, the rate itself (0.7) is also
    // inexact. Together these errors can push the result just below a .5 boundary and flip the
    // rounding decision (e.g. 75.075 → 75.07499... → rounds to 75.07 instead of 75.08).
    //
    // The safe approach is to work in integer cents throughout:
    //   - Math.round(prevTotal * 100) snaps the accumulated float noise to an exact integer.
    //   - prevTotalCents * (100 - discountPercent) is integer × integer, always exact.
    //   - The only X.5 case after / 100 is when the remainder is exactly 50, and 50/100 = 0.5
    //     which IS exactly representable, so Math.round is never at risk of a float error there.
    const prevTotalCents = Math.round(processedItems.ecApplied.prevTotal * 100);
    const discountedCents = Math.round((prevTotalCents * (100 - discountPercent)) / 100);
    processedItems.ecApplied.total = discountedCents / 100;
  }

  return processedItems;
};
